/**
 * `useAccountMe` — reactive accessor for the signed-in account row.
 *
 * Returns the authenticated account's `id` + optional `username`.
 * Used by the username-claim modal, the account menu, and the
 * author-filter on the stream.
 *
 * Joins the shared `/v1/bootstrap` singleton instead of firing its
 * own `GET /v1/account/me` request. Bootstrap already returns the
 * account row, so on cold load we'd otherwise pay for two trips
 * across the wire for the same data — the standalone `/account/me`
 * call was leftover from before the bootstrap aggregator landed.
 *
 * Cached aggressively (`staleTime: Infinity`) since the row only
 * changes via the username-claim flow. That mutation pushes the
 * fresh row straight into this query's cache via
 * `queryClient.setQueryData(ACCOUNT_ME_QUERY_KEY, claimed)` so we
 * don't need an invalidate + refetch round-trip — the bootstrap
 * promise is a frozen snapshot of cold-load state and would return
 * the old row on a re-read anyway.
 */
import { useQuery } from "@tanstack/react-query";
import { getBootstrapPromise } from "@/api/bootstrap-api";
export const ACCOUNT_ME_QUERY_KEY = ["account.me"];
export function useAccountMe() {
    const query = useQuery({
        queryKey: ACCOUNT_ME_QUERY_KEY,
        queryFn: async () => {
            const payload = await getBootstrapPromise();
            return payload.account;
        },
        staleTime: Number.POSITIVE_INFINITY,
    });
    return {
        account: query.data,
        isLoading: query.isLoading,
        hasUsername: query.data?.username !== undefined,
        refetch: () => {
            void query.refetch();
        },
    };
}
